"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Search, Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.slice(0, 5).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="hover:!bg-transparent focus:!bg-transparent relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] hover:after:w-full focus:after:w-full px-4 py-2 text-sm font-medium">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" disabled>
        <Menu className="size-5" />
      </Button>;
  }
  return <>
      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {isOpen && <div className="absolute top-full left-0 right-0 bg-dark-background border-t border-primary/20 shadow-medium md:hidden">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {menuItems.slice(0, 5).map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="px-4 py-3 rounded-md hover:bg-primary/10 focus:bg-primary/10 transition-all duration-[var(--duration-medium)] text-foreground-dark font-medium" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-dark-background/95 backdrop-blur-md border-b border-primary/20 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${scrolled ? "h-16 shadow-medium" : "h-24"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between gap-6 !text-[hsl(0,0%,100%)]">
        <Link to="/" className="shrink-0 hover:!bg-transparent focus:!bg-transparent">
          <span className="font-display text-primary tracking-tight !font-extrabold !text-3xl">
            Agentic Artist
          </span>
        </Link>

        <MenuProvider menu_id="17" className="flex-1 flex justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="flex items-center gap-3">
          <div className="relative hidden sm:flex items-center">
            <Input type="search" placeholder="Search..." className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] bg-dark-background/50 border-primary/30 text-foreground-dark placeholder:text-muted-foreground/60 focus:border-primary focus:w-64 ${searchOpen ? "w-64" : "w-10"}`} onFocus={() => setSearchOpen(true)} onBlur={() => setSearchOpen(false)} />
            <Search className="absolute right-3 size-4 text-muted-foreground/60 pointer-events-none" />
          </div>

          <Button className="hidden md:inline-flex hover:!bg-primary-hover focus:!bg-primary-hover active:!bg-primary-active hover:scale-[1.04] active:scale-100 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)] shadow-[0_0_15px_rgba(59,130,246,0.5)] hover:shadow-[0_0_25px_rgba(59,130,246,0.7)] focus:ring-2 focus:ring-primary" asChild>
            <Link to="/AuditPage">Get Started</Link>
          </Button>

          <MenuProvider menu_id="17">
            <MobileMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}